import axios from './axios';
import { useSessionStore } from '@stores/session';
import { ConfigService } from '@services/ConfigService';
import { unmarshalDynamoDBDocumentGood, type DynamoDBItem } from '@services/dynamodbService';
import { notify } from '@components/NotificationPlugin/';
import { readApiError } from '@utils/errorMessages';
import { getTranslation } from '../i18nlocal';

interface AxiosConfig {
  params: Record<string, unknown>;
  method: string;
  url: string;
  data?: unknown;
}

class AutoService {
  // appConfig can be null briefly (first load, hostname refetch) — wait for
  // it instead of crashing on null.domain.
  private static async baseConfig() {
    let appConfig = useSessionStore().appConfig;
    if (!appConfig?.domain) {
      appConfig = await ConfigService.loadConfig(null, null);
    }
    if (!appConfig?.domain) {
      throw new Error('App configuration is not loaded yet');
    }
    return appConfig;
  }

  static async delete(what: string, id?: string | number | null) {
    try {
      const baseURLs = await AutoService.baseConfig();

      let url = `${baseURLs.domain}api/${what}`;
      if (id) {
        url += `/${encodeURIComponent(String(id))}`;
      }

      const response = await axios({
        method: 'DELETE',
        url,
      });

      return response.data;
    } catch (error) {
      // Check for specific error responses and notify
      notify('danger', readApiError(error).message || getTranslation('errors.delete'));
      throw error;
    }
  }

  static async post(what: string, id?: string | number | null, data?: unknown) {
    const baseURLs = await AutoService.baseConfig();

    let url = `${baseURLs.domain}api/${what}`;
    if (id) {
      url += `/${encodeURIComponent(String(id))}`;
    }
    const response = await axios({
      method: 'POST',
      url,
      data,
    });
    return response.data;
  }

  static async getInvoiceUrl(uniqueNumber: string) {
    try {
      const baseURLs = await AutoService.baseConfig();
      const url = `${baseURLs.domain}api/invoice?invoiceNumber=${uniqueNumber}&invoiceType=PDF`;
      const response = await axios({
        method: 'GET',
        url,
      });
  
      return response.data;
    } catch (error) {
      console.error('Failed to fetch invoice:', error);
      throw error;
    }
  }

  static async downloadInvoice(pdfUrl: string) {
    try {
      const baseURLs = await AutoService.baseConfig();
      const url = `${baseURLs.domain}api/download-invoice`;
      const response = await axios({
        method: 'POST',
        url,
        data: { pdfUrl },
        responseType: 'blob',
      });
      
      return response.data;
    } catch (error) {
      notify('danger', readApiError(error).message || 'Error downloading invoice');
      throw error;
    }
  }

  static async uploadToS3(what: string, data: unknown) {
    const baseURLs = await AutoService.baseConfig();
    const url = `${baseURLs.domain}api/${what}`;
    try
    {
      const response = await axios({
        method: 'PUT',
        url,
        data,
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    }
    catch (error) {
      const apiError = readApiError(error);
      if (apiError.status === 400 && apiError.message) {
        notify('danger', apiError.message);
      }
      else
      {
        notify('danger', getTranslation('errors.upload'));
      }
      throw error;
    }
  }

  static async getfromS3(what: string) {
    const baseURLs = await AutoService.baseConfig();
    const url = `${baseURLs.domain}api/${what}`;
    try
    {
      const response = await axios({
        method: 'GET',
        url,
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    }
    catch (error) {
      const apiError = readApiError(error);
      if (apiError.status === 400 && apiError.message) {
        notify('danger', apiError.message);
      }
      else
      {
        notify('danger', getTranslation('errors.upload'));
      }
      throw error;
    }
  }


  
  static async fetchOrder(what: string, id?: string | number | null, data?: unknown, query?: Record<string, string | number | boolean>) {
    try {
      const baseURLs = await AutoService.baseConfig();
      let url = `${baseURLs.domain}api/${what}`;
      
      if (id) {
        url += `/${encodeURIComponent(String(id))}`;
      }

      if (query) {
        const params = new URLSearchParams();
        for (const [key, value] of Object.entries(query)) params.append(key, String(value));
        url += `?${params.toString()}`;
      }
      
      let method: string, requestData: unknown;
      if (data) {
        if (id) {
          method = 'PUT';
          requestData = data;
        } else {
          method = 'POST';
          requestData = data;
        }
      } else {
        method = 'GET';
      }
  
      const axiosConfig: AxiosConfig = {
        method,
        url,
        params: {},
      };
  
      if (requestData) {
        axiosConfig.data = requestData;
      }
  
      let response = null;
      try {
        response = await axios(axiosConfig);
      } catch (error) {
        const apiError = readApiError(error);
        if (apiError.status === 400 && apiError.message) {
          throw new Error(apiError.message);
        }
        throw error;
      }
  
      if (method === 'POST' || method === 'PUT') {
        return unmarshalDynamoDBDocumentGood(response.data);
      } else {
        // Check for DynamoDB errors
        if (response.data?.__type) {
          switch (response.data.__type) {
            case 'com.amazon.coral.validate#ValidationException':
              throw new Error(`Validation Error: ${response.data.message}`);
            case 'com.amazonaws.dynamodb.v20120810#ResourceNotFoundException':
              throw new Error(`Resource Not Found: ${response.data.message}`);
            case 'com.amazonaws.dynamodb.v20120810#ProvisionedThroughputExceededException':
              throw new Error(`Throughput Exceeded: ${response.data.message}`);
            case 'com.amazon.coral.service#SerializationException':
              throw new Error(`Serialization Error: ${response.data.message}`);
            case 'com.amazonaws.dynamodb.v20120810#InternalServerError':
              throw new Error(`DynamoDB Internal Error: ${response.data.message}`);
            default:
              throw new Error(`DynamoDB Error: ${response.data.__type} - ${response.data.message}`);
          }
        }
       
        if (response.data !== undefined) {
          if (response.data.uploadUrl || response.data.fileUrl || response.data.ElementItem) {
            return response.data;
          }

          let resultData: DynamoDBItem[] = [];
          try {
            
            const unmarshalledData = unmarshalDynamoDBDocumentGood(response.data);
            if (Array.isArray(unmarshalledData)) {
              resultData = unmarshalledData;
            } else if (typeof unmarshalledData === 'object' && unmarshalledData !== null) {
              resultData = unmarshalledData;
            } else if (unmarshalledData === null || unmarshalledData === undefined) {
              resultData = [response.data];
            }
          } catch (unmarshalError) {
            console.error('Error unmarshalling DynamoDB document:', unmarshalError);
            resultData = [response.data];
          }

          return resultData;
          
        } else {
          return response;
        }
      }
    } catch (error) {
      // Normalize non-Error rejections; Error instances pass through untouched.
      if (error instanceof Error) throw error;
      throw new Error(String(error));
    }
  }

    
}

export default AutoService;
